import _ from 'underscore';

const _getPageItem = page => {
    return [page.id, page.name];
};

const _getWidgetItem = widget => {
    return [widget.id, widget.name, widget.type, widget.properties];
};

const _getVariable = variable => {
    return [variable.name, variable.id, variable.description, variable.defaultValue];
}

const _getXSightTopic = topic => {
    return [topic.name, topic.defaultValue];
}

export const getAllPagesFromContext = context => {
    return context.pages.map(page => _getPageItem(page))
};

// Returns null when no widget matches, so callers keep their current columns.
// Ids are coerced because older saved projects can store them as numbers.
export const getWidgetColumnOptions = (context, widgetId, emptyOptionLabel) => {
    for (const page of context.pages) {
        const widget = page.widgets.find(w => String(w.id) === String(widgetId));
        if (widget) {
            return {
                columns: widget.columns,
                options: widget.columns.some(Boolean)
                    ? widget.columns.map(column => [column.name, column.id])
                    : [[emptyOptionLabel, 'ColName']]
            };
        }
    }
    return null;
};

export const getAllWidgetsFromContext = context => {
    return context.pages.map(page => {
        return getAllWidgetsInPageObject(page);
    });
};

export const getAllTypeWidgetsFromContext = (context, type) => {
    return context.pages.map(page => {
        return getTypeWidgetsInPageObject(page, type);
    });
};

export const getAllVariablesFromContext = (context) => {
    return context.environmentVariables.map(variable => _getVariable(variable));
}

export const getAllXSightTopicsFromContext = (context) => {
    return context.xSightTopics.map(topic => _getXSightTopic(topic));
}

export const getPageFromContext = (context, query) => {
    return _.findWhere(context.pages, query);
};

export const getPageWidgetsFromContext = (context, query) => {
    const page = getPageFromContext(context, query);
    return getAllWidgetsInPageObject(page);
};

export const getPageTypeWidgetsFromContext = (context, query, type) => {
    const page = getPageFromContext(context, query);
    return getTypeWidgetsInPageObject(page, type);
};

export const getAllWidgetsInPageObject = page => {
    return page.widgets.map(widget => _getWidgetItem(widget));
};

export const getTypeWidgetsInPageObject = (page, type) => {
    return page.widgets.reduce((allWidgets, widget) => {
        if (widget.type === type) {
            allWidgets.push(_getWidgetItem(widget));
        }
        return allWidgets;
    }, []);
};

export const _defineHelpUrlandToolTip = (variableName, helpUrl, toolTip) => {

    if (Blockly.Blocks[variableName]) {
        const originalInit = Blockly.Blocks[variableName].init;
        Blockly.Blocks[variableName].init = function () {
            originalInit.call(this);
            this.setHelpUrl(helpUrl);
            if (toolTip) {
                this.setTooltip(toolTip);
            }
        };
    }

}

function trim(str, len) {
    if (str.length <= len) return str;
    let halfLen = Math.floor(len / 2);
    return str.substring(0, halfLen - 2) + "..." + str.substring(str.length - halfLen - 1, str.length);
}

export const getPagesFromContext = context => {
    const contextPages = context.pages.concat(context.webPages);
    let pages = contextPages.filter(page => page && (!page.formId || !page.formId.includes('carouselgroup')));

    pages = pages.sort(function (a, b) {
        let aName = a.name;
        let bName = b.name;
        if (a.formName) {
            aName = a.formName + aName;
        }
        if (b.formName) {
            bName = b.formName + bName;
        }
        return (aName.toLowerCase() > bName.toLowerCase() ? 1 : -1);
    });

    if (!!context.workflowItems) {
        pages = [...pages, ...context.workflowItems];
    }

    return pages.map(function (x) {
        let pageType = x.type;
        let displayName = x.name;
        let id = x.id;
        if (x.formName) {
            displayName = trim(x.formName, 13) + " > " + trim(x.name, 14);
            id = `${x.formDataGenyId}<<<>>>${x.id}`;
        } else {
            displayName = trim(displayName, 30);
        }
        return [displayName, id, pageType];
    });
};

export const getFormsFromContext = context => {
    let pages = context.pages.sort(function (a, b) {
        let aName = a.name;
        let bName = b.name;
        if (a.formName) {
            aName = a.formName + aName;
        }
        if (b.formName) {
            bName = b.formName + bName;
        }
        return (aName.toLowerCase() > bName.toLowerCase() ? 1 : (-1));
    });

    return pages.filter(p => p.formDataGenyId).map(function (x) {
        let displayName = trim(x.formName, 13) + " > " + trim(x.name, 14);
        return [displayName, `${x.formDataGenyId}<<<>>>${x.id}`]
    });
}

/**
 * Mark a block as deprecated
 * Adds deprecation notice, warning, and visual styling
 * 
 * @param {Object} blockInstance - The Blockly block instance (this)
 * @param {string} [customWarning] - Optional custom warning text shown to users (e.g., what block to use instead)
 */
export const markBlockAsDeprecated = (blockInstance, customWarning = null) => {
    const defaultWarning = "This block was deprecated. Please use the latest version from the toolbox.";
    blockInstance.isDeprecatedVersionBlock_ = true;
    
    // 1. Add deprecation notice inline
    blockInstance.appendDummyInput('DEPRECATED_NOTICE')
        .appendField(' - deprecated -');
    
    // 2. Set warning text (shown to users)
    blockInstance.setWarningText(customWarning || defaultWarning);
    
    // 3. Set opacity to visually indicate deprecated status
    blockInstance.setOpacity(0.6);
}